import {
  COLOUR_MODES,
  RESERVED_SEMANTIC_HUE_NAMES,
  SEMANTIC_COLOUR_TOKENS,
  TENANT_IDENTITY_COLOUR_POLICY,
  type ColourMode,
  type HexColour,
  type ReservedSemanticHue,
} from '../design-tokens/source';

export type TenantColourFallbackReason =
  | 'accepted'
  | 'missing'
  | 'invalid_format'
  | 'reserved_hue_collision'
  | 'insufficient_surface_contrast';

export type TenantColourAssessmentStatus =
  | 'accepted'
  | 'missing'
  | 'invalid_format'
  | 'reserved_hue_collision'
  | 'insufficient_surface_contrast';

export interface TenantColourModeResolution {
  readonly colour: HexColour;
  readonly source: 'tenant' | 'canonical_fallback';
  readonly reason: TenantColourFallbackReason;
  readonly contrastRatioAgainstSurfaceBase: number;
  readonly closestReservedHue: ReservedSemanticHue | null;
  readonly closestReservedDeltaE00: number | null;
}

export interface TenantIdentityColourAssessment {
  readonly policyVersion: typeof TENANT_IDENTITY_COLOUR_POLICY.version;
  readonly status: TenantColourAssessmentStatus;
  readonly submitted: string | null;
  readonly normalised: HexColour | null;
  readonly byMode: Readonly<Record<ColourMode, TenantColourModeResolution>>;
}

interface LabColour {
  readonly l: number;
  readonly a: number;
  readonly b: number;
}

export function normaliseHexColour(input: unknown): HexColour | null {
  if (typeof input !== 'string') return null;
  const trimmed = input.trim();
  const match = /^#([0-9A-F]{6})$/iu.exec(trimmed);
  return match?.[1] ? (`#${match[1].toUpperCase()}` as HexColour) : null;
}

function hexToRgb(hex: HexColour): readonly [number, number, number] {
  return [
    Number.parseInt(hex.slice(1, 3), 16),
    Number.parseInt(hex.slice(3, 5), 16),
    Number.parseInt(hex.slice(5, 7), 16),
  ];
}

function lineariseSrgb(channel: number): number {
  const normalised = channel / 255;
  return normalised <= 0.04045 ? normalised / 12.92 : ((normalised + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(hex: HexColour): number {
  const [red, green, blue] = hexToRgb(hex).map(lineariseSrgb) as [number, number, number];
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

export function contrastRatio(foreground: HexColour, background: HexColour): number {
  const foregroundLuminance = relativeLuminance(foreground);
  const backgroundLuminance = relativeLuminance(background);
  const lighter = Math.max(foregroundLuminance, backgroundLuminance);
  const darker = Math.min(foregroundLuminance, backgroundLuminance);
  return (lighter + 0.05) / (darker + 0.05);
}

function pivotXyz(value: number): number {
  const delta = 6 / 29;
  return value > delta ** 3 ? Math.cbrt(value) : value / (3 * delta ** 2) + 4 / 29;
}

function hexToLab(hex: HexColour): LabColour {
  const [red, green, blue] = hexToRgb(hex).map(lineariseSrgb) as [number, number, number];

  const x = (red * 0.4124564 + green * 0.3575761 + blue * 0.1804375) / 0.95047;
  const y = red * 0.2126729 + green * 0.7151522 + blue * 0.072175;
  const z = (red * 0.0193339 + green * 0.119192 + blue * 0.9503041) / 1.08883;

  const fx = pivotXyz(x);
  const fy = pivotXyz(y);
  const fz = pivotXyz(z);

  return {
    l: 116 * fy - 16,
    a: 500 * (fx - fy),
    b: 200 * (fy - fz),
  };
}

const degreesToRadians = (degrees: number): number => (degrees * Math.PI) / 180;
const radiansToDegrees = (radians: number): number => (radians * 180) / Math.PI;

function hueDegrees(a: number, b: number): number {
  if (a === 0 && b === 0) return 0;
  const degrees = radiansToDegrees(Math.atan2(b, a));
  return degrees >= 0 ? degrees : degrees + 360;
}

/** CIEDE2000, with all weighting factors fixed at the standard value of 1. */
export function deltaE00(left: HexColour, right: HexColour): number {
  const lab1 = hexToLab(left);
  const lab2 = hexToLab(right);
  const c1 = Math.hypot(lab1.a, lab1.b);
  const c2 = Math.hypot(lab2.a, lab2.b);
  const meanC = (c1 + c2) / 2;
  const meanC7 = meanC ** 7;
  const g = 0.5 * (1 - Math.sqrt(meanC7 / (meanC7 + 25 ** 7)));
  const a1Prime = (1 + g) * lab1.a;
  const a2Prime = (1 + g) * lab2.a;
  const c1Prime = Math.hypot(a1Prime, lab1.b);
  const c2Prime = Math.hypot(a2Prime, lab2.b);
  const h1Prime = hueDegrees(a1Prime, lab1.b);
  const h2Prime = hueDegrees(a2Prime, lab2.b);

  const deltaLPrime = lab2.l - lab1.l;
  const deltaCPrime = c2Prime - c1Prime;
  const rawHueDifference = h2Prime - h1Prime;
  const deltaHuePrime =
    c1Prime * c2Prime === 0
      ? 0
      : Math.abs(rawHueDifference) <= 180
        ? rawHueDifference
        : rawHueDifference > 180
          ? rawHueDifference - 360
          : rawHueDifference + 360;
  const deltaHPrime =
    2 * Math.sqrt(c1Prime * c2Prime) * Math.sin(degreesToRadians(deltaHuePrime / 2));

  const meanLPrime = (lab1.l + lab2.l) / 2;
  const meanCPrime = (c1Prime + c2Prime) / 2;
  const meanHPrime =
    c1Prime * c2Prime === 0
      ? h1Prime + h2Prime
      : Math.abs(h1Prime - h2Prime) <= 180
        ? (h1Prime + h2Prime) / 2
        : h1Prime + h2Prime < 360
          ? (h1Prime + h2Prime + 360) / 2
          : (h1Prime + h2Prime - 360) / 2;

  const t =
    1 -
    0.17 * Math.cos(degreesToRadians(meanHPrime - 30)) +
    0.24 * Math.cos(degreesToRadians(2 * meanHPrime)) +
    0.32 * Math.cos(degreesToRadians(3 * meanHPrime + 6)) -
    0.2 * Math.cos(degreesToRadians(4 * meanHPrime - 63));
  const deltaTheta = 30 * Math.exp(-(((meanHPrime - 275) / 25) ** 2));
  const meanCPrime7 = meanCPrime ** 7;
  const rC = 2 * Math.sqrt(meanCPrime7 / (meanCPrime7 + 25 ** 7));
  const sL = 1 + (0.015 * (meanLPrime - 50) ** 2) / Math.sqrt(20 + (meanLPrime - 50) ** 2);
  const sC = 1 + 0.045 * meanCPrime;
  const sH = 1 + 0.015 * meanCPrime * t;
  const rT = -Math.sin(degreesToRadians(2 * deltaTheta)) * rC;

  const lightnessTerm = deltaLPrime / sL;
  const chromaTerm = deltaCPrime / sC;
  const hueTerm = deltaHPrime / sH;

  return Math.sqrt(lightnessTerm ** 2 + chromaTerm ** 2 + hueTerm ** 2 + rT * chromaTerm * hueTerm);
}

function closestReservedHue(
  colour: HexColour,
  mode: ColourMode,
): { readonly name: ReservedSemanticHue; readonly delta: number } {
  let closestName: ReservedSemanticHue = RESERVED_SEMANTIC_HUE_NAMES[0];
  let closestDelta = Number.POSITIVE_INFINITY;

  for (const name of RESERVED_SEMANTIC_HUE_NAMES) {
    const delta = deltaE00(colour, SEMANTIC_COLOUR_TOKENS[mode][name]);
    if (delta < closestDelta) {
      closestName = name;
      closestDelta = delta;
    }
  }

  return { name: closestName, delta: closestDelta };
}

function fallbackResolution(
  mode: ColourMode,
  reason: Exclude<TenantColourFallbackReason, 'accepted'>,
  closest: ReturnType<typeof closestReservedHue> | null,
): TenantColourModeResolution {
  const fallback = SEMANTIC_COLOUR_TOKENS[mode]['identity.tenant.fallback'];
  return {
    colour: fallback,
    source: 'canonical_fallback',
    reason,
    contrastRatioAgainstSurfaceBase: contrastRatio(
      fallback,
      SEMANTIC_COLOUR_TOKENS[mode]['surface.base'],
    ),
    closestReservedHue: closest?.name ?? null,
    closestReservedDeltaE00: closest?.delta ?? null,
  };
}

function allFallbackModes(
  reason: Exclude<TenantColourFallbackReason, 'accepted'>,
  closestByMode?: Partial<Record<ColourMode, ReturnType<typeof closestReservedHue>>>,
): Readonly<Record<ColourMode, TenantColourModeResolution>> {
  return Object.fromEntries(
    COLOUR_MODES.map((mode) => [
      mode,
      fallbackResolution(mode, reason, closestByMode?.[mode] ?? null),
    ]),
  ) as unknown as Readonly<Record<ColourMode, TenantColourModeResolution>>;
}

export function assessTenantIdentityColour(input: unknown): TenantIdentityColourAssessment {
  const submitted = typeof input === 'string' ? input : null;
  if (input === null || input === undefined || submitted?.trim() === '') {
    return {
      policyVersion: TENANT_IDENTITY_COLOUR_POLICY.version,
      status: 'missing',
      submitted,
      normalised: null,
      byMode: allFallbackModes('missing'),
    };
  }

  const normalised = normaliseHexColour(input);
  if (!normalised) {
    return {
      policyVersion: TENANT_IDENTITY_COLOUR_POLICY.version,
      status: 'invalid_format',
      submitted,
      normalised: null,
      byMode: allFallbackModes('invalid_format'),
    };
  }

  const closestByMode = Object.fromEntries(
    COLOUR_MODES.map((mode) => [mode, closestReservedHue(normalised, mode)]),
  ) as unknown as Record<ColourMode, ReturnType<typeof closestReservedHue>>;

  if (
    COLOUR_MODES.some(
      (mode) =>
        closestByMode[mode].delta <
        TENANT_IDENTITY_COLOUR_POLICY.minimumDeltaE00FromEveryReservedHue,
    )
  ) {
    return {
      policyVersion: TENANT_IDENTITY_COLOUR_POLICY.version,
      status: 'reserved_hue_collision',
      submitted,
      normalised,
      byMode: allFallbackModes('reserved_hue_collision', closestByMode),
    };
  }

  const contrastByMode = Object.fromEntries(
    COLOUR_MODES.map((mode) => [
      mode,
      contrastRatio(normalised, SEMANTIC_COLOUR_TOKENS[mode]['surface.base']),
    ]),
  ) as unknown as Record<ColourMode, number>;

  if (
    COLOUR_MODES.some(
      (mode) =>
        contrastByMode[mode] < TENANT_IDENTITY_COLOUR_POLICY.minimumContrastRatioAgainstSurfaceBase,
    )
  ) {
    return {
      policyVersion: TENANT_IDENTITY_COLOUR_POLICY.version,
      status: 'insufficient_surface_contrast',
      submitted,
      normalised,
      byMode: allFallbackModes('insufficient_surface_contrast', closestByMode),
    };
  }

  return {
    policyVersion: TENANT_IDENTITY_COLOUR_POLICY.version,
    status: 'accepted',
    submitted,
    normalised,
    byMode: Object.fromEntries(
      COLOUR_MODES.map((mode) => [
        mode,
        {
          colour: normalised,
          source: 'tenant',
          reason: 'accepted',
          contrastRatioAgainstSurfaceBase: contrastByMode[mode],
          closestReservedHue: closestByMode[mode].name,
          closestReservedDeltaE00: closestByMode[mode].delta,
        } satisfies TenantColourModeResolution,
      ]),
    ) as unknown as Readonly<Record<ColourMode, TenantColourModeResolution>>,
  };
}
